import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { CosmicOrb } from "@/components/cosmic/CosmicOrb";
import { useReducedMotion } from "@/components/cosmic/useReducedMotion";
import { ChevronDown, Sparkles } from "lucide-react";
export default function Hero() {
  const prefersReduced = useReducedMotion();
  const [mouseOffset, setMouseOffset] = useState<{
    x: number;
    y: number;
  }>({
    x: 0,
    y: 0
  });

  // Headline words for staggered animated rise/glow reveal
  const headlineWords = ["Explore", "the", "Reality", "Beyond", "Reality."];

  // Optional mouse parallax for desktop users
  useEffect(() => {
    if (prefersReduced || typeof window === "undefined") return;

    // Only attach if fine pointer (mouse)
    const mediaQuery = window.matchMedia("(pointer: fine)");
    if (!mediaQuery.matches) return;
    const handleMouseMove = (e: MouseEvent) => {
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      const dx = (e.clientX - centerX) / centerX;
      const dy = (e.clientY - centerY) / centerY;
      setMouseOffset({
        x: dx * 14,
        y: dy * 14
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [prefersReduced]);
  return <section id="hero" data-nav="dark" className="relative min-h-[95svh] lg:min-h-[100svh] w-full max-w-full flex items-center justify-center overflow-x-clip text-foreground pt-20 sm:pt-24 pb-16">
      {/* Background Astrophotography Visualization - dimmed and layered */}
      <div className="absolute inset-0 pointer-events-none select-none z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/multiverse_visualization.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/multiverse_visualization.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/multiverse_visualization.png-wvc-sizes" alt="Swirling filaments of cosmic light and interconnected nebulae against deep space" width={1920} height={1080} className="h-full w-full object-cover object-center opacity-30" loading="eager" decoding="async" />
        {/* Scrim gradients strictly using background tokens to allow fixed starfield to shine */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/40 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,var(--background)_80%)]" />
      </div>

      {/* Main Hero Container */}
      <div className="relative z-10 mx-auto w-full max-w-[76rem] px-4 sm:px-6 py-12 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Column (Headline, copy, CTAs) */}
          <div className="lg:col-span-7 flex flex-col items-start">
            {/* Signature Celestial Coordinate Eyebrow */}
            <div className="flex items-center gap-3 mb-6 w-full max-w-xs sm:max-w-sm">
              <div className="h-px flex-1 bg-border" />
              <div className="size-1.5 rotate-45 bg-primary shadow-[0_0_8px_var(--glow-primary)]" />
              <div className="h-px flex-1 bg-border" />
            </div>

            {/* Eyebrow Stamp */}
            <div className="inline-flex items-center gap-2 mb-4 font-mono text-xs uppercase tracking-[0.18em] text-primary">
              <Sparkles className="size-3.5" />
              <span>THE MULTIVERSE INQUIRY</span>
            </div>

            {/* Word-by-word staggered rise/glow reveal headline */}
            <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl lg:text-[4.25rem] font-extrabold leading-[1.08] tracking-[0.03em] text-foreground mb-6 break-words">
              {headlineWords.map((word, index) => {
              if (prefersReduced) {
                return <span key={index} className="inline-block mr-3">
                      {word}
                    </span>;
              }
              return <motion.span key={index} initial={{
                opacity: 0,
                y: 24,
                filter: "blur(6px)"
              }} animate={{
                opacity: 1,
                y: 0,
                filter: "blur(0px)"
              }} transition={{
                duration: 0.65,
                delay: 0.15 + index * 0.1,
                ease: [0.16, 1, 0.3, 1]
              }} className={`inline-block mr-3 sm:mr-4 ${word === "Reality." ? "text-brand-gradient drop-shadow-[0_0_24px_var(--glow-primary)]" : ""}`}>
                    {word}
                  </motion.span>;
            })}
            </h1>

            {/* Supporting Prose */}
            <motion.p initial={prefersReduced ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.7,
            ease: "easeOut"
          }} className="font-sans text-sm sm:text-base lg:text-xl text-muted-foreground leading-relaxed max-w-2xl mb-8 sm:mb-10">
              Discover quantum mysteries, explore parallel-universe theories, experiment with the strange laws of physics, and journey through the biggest unanswered questions about existence.
            </motion.p>

            {/* Dual CTA Button Group */}
            <motion.div initial={prefersReduced ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.85,
            ease: "easeOut"
          }} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <Link to="/MultiverseAtlasPage" className="ql-btn w-full sm:w-auto min-h-[44px] justify-center">
                <span>Enter the Multiverse</span>
              </Link>

              <Link to="/QuantumLabPage" className="ql-btn-ghost w-full sm:w-auto min-h-[44px] justify-center">
                <span>Explore Quantum Physics</span>
              </Link>
            </motion.div>
          </div>

          {/* Right Column: Floating Cosmic Orb with orbital coordinate rings */}
          <div className="lg:col-span-5 hidden lg:flex items-center justify-center relative">
            <motion.div style={{
            transform: prefersReduced ? "none" : `translate(${mouseOffset.x}px, ${mouseOffset.y}px)`,
            transition: "transform 250ms ease-out"
          }} className="relative p-8">
              {/* Outer atmospheric aura */}
              <div aria-hidden="true" className="absolute inset-0 rounded-full bg-primary/10 blur-3xl pointer-events-none" />

              {/* CosmicOrb */}
              <CosmicOrb size={220} hue="cyan" rings={3} className="ql-float" />

              {/* Telemetry coordinate chip */}
              <div className="absolute -bottom-2 right-4 rounded-lg bg-card/80 border border-border/60 px-3 py-1.5 backdrop-blur-md text-[11px] font-mono text-muted-foreground shadow-lg">
                <span className="text-primary font-bold">HORIZON:</span> 46.5 GLY
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Subtle "Scroll" Cue Linking to #portals */}
      <div className="absolute bottom-6 inset-x-0 flex justify-center z-20 pointer-events-auto">
        <Link to="#portals" aria-label="Scroll to exploratory portals" className="group inline-flex flex-col items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors duration-200">
          <span>Explore Portals</span>
          <ChevronDown className="size-4 animate-bounce text-primary/80 group-hover:text-primary" />
        </Link>
      </div>
    </section>;
}